import {
  type FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { ErrorModal } from "@/ui/common/components/Modals/ErrorModal";
import { Error as AppError, ErrorHandlerParam } from "@/ui/common/types/errors";

const ErrorContext = createContext<ErrorContextType>({
  isOpen: false,
  error: {
    message: "",
  },
  modalOptions: {},
  dismissError: () => {},
  handleError: () => {},
});

interface ErrorProviderProps {
  children: ReactNode;
}

type ErrorState = {
  isOpen: boolean;
  error: AppError;
  modalOptions: {
    retryAction?: () => void;
    noCancel?: boolean;
  };
};

export type ErrorContextType = ErrorState & {
  dismissError: () => void;
  handleError: (param: ErrorHandlerParam) => void;
};

export const ErrorProvider: FC<ErrorProviderProps> = ({ children }) => {
  const [state, setState] = useState<ErrorState>({
    isOpen: false,
    error: { message: "" },
    modalOptions: {},
  });

  const dismissError = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const handleError = useCallback(
    ({ error, displayOptions, metadata }: ErrorHandlerParam) => {
      if (!error) return;

      // Extract stack trace if available
      const stackTrace = error instanceof Error ? error.stack || "" : "";

      const shouldShowModal = displayOptions?.showModal ?? true;

      const errorData = {
        message: error.message,
        trace: stackTrace,
        ...metadata,
      };

      if (shouldShowModal) {
        setState((prev) => {
          // Prevent flicker: if modal is already open with the same error message, don't update
          if (prev.isOpen && prev.error.message === errorData.message) {
            return prev;
          }
          return {
            isOpen: true,
            error: errorData,
            modalOptions: {
              ...displayOptions,
            },
          };
        });
      }
    },
    [],
  );

  const contextValue = useMemo(
    () => ({
      ...state,
      dismissError,
      handleError,
    }),
    [state, dismissError, handleError],
  );

  return (
    <ErrorContext.Provider value={contextValue}>
      {children}
      <ErrorModal />
    </ErrorContext.Provider>
  );
};
export const useError = () => useContext(ErrorContext);
